import { inject, Injectable } from '@angular/core';
import { NavigationCancel, NavigationError, Router } from '@angular/router';

import { AnalyticsService } from './analytics.service';
import {
  buildRouteName,
  currentLocationPath,
  sanitizePath,
} from './rum-route.util';

/** Name of the custom event emitted for crash-adjacent frontend signals. */
export const FRONTEND_ERROR_EVENT_NAME = 'FrontendError';

/** Kind of crash-adjacent signal, reported as the `errorType` dimension. */
export type FrontendErrorType =
  | 'error'
  | 'unhandledrejection'
  | 'navigation_error'
  | 'navigation_cancel_burst';

/** How many cancels within {@link CANCEL_WINDOW_MS} count as a crash-adjacent burst. */
const CANCEL_BURST_THRESHOLD = 3;

/** Sliding window (ms) for detecting a burst of navigation cancels. */
const CANCEL_WINDOW_MS = 10_000;

/** Max characters of an error message kept on the event. */
const MAX_MESSAGE_LENGTH = 300;

/**
 * Emits a dedicated, queryable **crash-adjacent** signal with route/session
 * context, so support can separate browser-side crash-like failures from
 * backend issues.
 *
 * App Insights already auto-captures uncaught exceptions and unhandled
 * rejections as `exceptions`; this adds a single `FrontendError` custom event
 * that unifies those with **navigation failures** (`NavigationError`) and a
 * **burst of navigation cancels** — none of which sit in the exceptions table —
 * and tags each with the route, so the freeze/crash dashboard can slice them
 * together.
 *
 * Note: Angular zone errors are routed to Sentry by the app's global
 * `ErrorHandler`; this service covers the window-level and navigation signals
 * App Insights sees, not that Sentry path.
 */
@Injectable()
export class FrontendErrorService {
  private analytics = inject(AnalyticsService);

  private router = inject(Router, { optional: true });

  private initialized = false;

  /** Recent navigation-cancel timestamps, for burst detection. */
  private cancelTimes: number[] = [];

  /**
   * Starts listening for crash-adjacent signals. Safe to call multiple times;
   * only the first call registers listeners.
   */
  public init(): void {
    if (this.initialized) {
      return;
    }
    this.initialized = true;

    window.addEventListener('error', (event: ErrorEvent) => {
      // Ignore resource-load errors (no Error/message) — only script errors.
      const message = event.error?.message ?? event.message;
      if (!message) {
        return;
      }
      this.report('error', message);
    });

    window.addEventListener(
      'unhandledrejection',
      (event: PromiseRejectionEvent) => {
        this.report('unhandledrejection', this.reasonMessage(event.reason));
      },
    );

    this.router?.events.subscribe((event) => {
      if (event instanceof NavigationError) {
        const message =
          event.error instanceof Error
            ? event.error.message
            : String(event.error);
        this.report('navigation_error', message);
      } else if (event instanceof NavigationCancel) {
        this.recordCancel();
      }
    });
  }

  /**
   * Reports an error caught by Angular's global `ErrorHandler` as a crash
   * signal. Most app errors are caught by Angular's zone and never reach
   * `window.onerror`, so the app's `ErrorHandler` forwards them here to make
   * them visible in App Insights. Safe to call without `init()`.
   * @param error
   */
  public reportError(error: unknown): void {
    const message =
      error instanceof Error ? error.message : String(error ?? '');
    this.report('error', message);
  }

  /**
   * Records a navigation cancel and emits a burst signal once too many happen
   * within the sliding window (a stuck/looping navigation, crash-adjacent).
   */
  private recordCancel(): void {
    const now = performance.now();
    this.cancelTimes = this.cancelTimes.filter(
      (time) => now - time < CANCEL_WINDOW_MS,
    );
    this.cancelTimes.push(now);
    if (this.cancelTimes.length >= CANCEL_BURST_THRESHOLD) {
      this.cancelTimes = [];
      this.report('navigation_cancel_burst');
    }
  }

  /**
   * Emits the crash-adjacent event tagged with route/visibility context.
   * @param errorType kind of signal
   * @param message optional error message (truncated)
   */
  private report(errorType: FrontendErrorType, message?: string): void {
    const properties: Record<string, string | number | undefined> = {
      errorType,
      routeName: buildRouteName(this.router),
      page: sanitizePath(currentLocationPath()),
      visibilityState:
        typeof document !== 'undefined' ? document.visibilityState : undefined,
    };
    if (message) {
      properties['message'] = message.slice(0, MAX_MESSAGE_LENGTH);
    }

    this.analytics.trackEvent({
      name: FRONTEND_ERROR_EVENT_NAME,
      properties,
    });
  }

  /**
   * Extracts a readable message from a promise rejection reason.
   * @param reason
   */
  private reasonMessage(reason: unknown): string {
    if (reason instanceof Error) {
      return reason.message;
    }
    return typeof reason === 'string' ? reason : JSON.stringify(reason);
  }
}
